/**
 * Far & Away video player and dialog. Nothing is fetched from the video host until a
 * player is loaded (normally when a play button is pressed).
 *
 * <fa-video-player data-src="https://vimeo.com/655409315" data-title="…" [data-autoload]>
 *   Any Vimeo page or player link, YouTube link, or .mp4 URL. For Vimeo the player is
 *   embedded with its own controls switched off (controls=0) and ours on top: tap or click
 *   to play and pause, a thin progress line that can be clicked to seek, a mute toggle and a
 *   fullscreen toggle. The film opens clean: the controls stay hidden until the pointer moves
 *   over it, it is tapped, or it gets keyboard focus, and they fade 2.5 s later while it plays
 *   (keyboard focus inside keeps them). They also show when the film is paused or ends, or if
 *   it has not started playing a few seconds after loading (autoplay blocked).
 *   YouTube keeps its own controls. An .mp4 file gets the same controls as Vimeo.
 *   Methods: load(), unload(), play(), pause(), toggle(), toggleMute(), toggleFullscreen().
 *   Events: fa-video:ready, fa-video:play, fa-video:pause, fa-video:ended (bubbling).
 *
 *   The poster (an <img class="fa-video-player__poster"> inside the element) covers the film
 *   until it actually plays, then fades out, so there is never a black frame.
 *   warm() loads a Vimeo or file player paused, ahead of time; start() (or load()) plays.
 *
 * <fa-video-dialog> wraps a <dialog> that contains an <fa-video-player>. Any element with
 *   data-fa-video-open="{dialog id}" opens it. Pointing at, touching or focusing that element
 *   warms the player and the poster, so by the time the dialog opens the film is ready; the
 *   player unloads on close. Methods: warm(), open(), close().
 *
 * Both elements register once, so another block can include this module and reuse them.
 */

const VIMEO_ORIGIN = 'https://player.vimeo.com';

/**
 * @param {string} src
 * @returns {{ kind: 'vimeo' | 'youtube' | 'file' | '', url: string }}
 */
export function resolveVideo(src, { autoplay = true } = {}) {
  const value = (src || '').trim();
  if (!value) return { kind: '', url: '' };

  const vimeo = value.match(/vimeo\.com\/(?:video\/)?(\d+)(?:\/([a-z0-9]+))?/i);
  if (vimeo) {
    const params = new URLSearchParams({
      controls: '0',
      autoplay: autoplay ? '1' : '0',
      title: '0',
      byline: '0',
      portrait: '0',
      dnt: '1',
      playsinline: '1',
    });
    const hash = vimeo[2] || new URL(value, location.href).searchParams.get('h');
    if (hash) params.set('h', hash);
    return { kind: 'vimeo', url: `${VIMEO_ORIGIN}/video/${vimeo[1]}?${params}` };
  }

  const youtube = value.match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([\w-]{6,})/);
  if (youtube) {
    return { kind: 'youtube', url: `https://www.youtube-nocookie.com/embed/${youtube[1]}?autoplay=1&rel=0&playsinline=1` };
  }

  if (/\.(mp4|webm|mov)(\?|$)/i.test(value)) return { kind: 'file', url: value };
  return { kind: '', url: '' };
}

const ICONS = {
  play: '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M8 5.8v12.4a.6.6 0 0 0 .9.5l9.9-6.2a.6.6 0 0 0 0-1L8.9 5.3a.6.6 0 0 0-.9.5Z"/></svg>',
  pause: '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M7 5h3.5v14H7zM13.5 5H17v14h-3.5z"/></svg>',
  sound: '<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 9.5h3.5L12 5.5v13l-4.5-4H4z"/><path d="M15.5 9a4 4 0 0 1 0 6M18 6.5a7.5 7.5 0 0 1 0 11"/></svg>',
  expand: '<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5"/></svg>',
  collapse: '<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5"/></svg>',
  muted: '<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 9.5h3.5L12 5.5v13l-4.5-4H4z"/><path d="m16 9.5 5 5M21 9.5l-5 5"/></svg>',
};

export class FaVideoPlayer extends HTMLElement {
  /** @type {HTMLIFrameElement | HTMLVideoElement | null} */
  #media = null;
  #kind = '';
  #playing = false;
  #muted = false;
  #duration = 0;
  /** @type {number | undefined} */
  #hideTimer;
  /** @type {number | undefined} */
  #startTimer;
  /** Whether the controls were out of sight when the current tap began */
  #tapRevealsOnly = false;
  /** @type {{ x: number, y: number } | null} */
  #lastPointer = null;
  /** The host player has said it is ready to take commands */
  #ready = false;
  /** Play as soon as the player is ready (start() came before it was) */
  #wantPlay = false;

  connectedCallback() {
    if (this.hasAttribute('data-autoload')) this.load();
  }

  disconnectedCallback() {
    this.unload();
  }

  get isLoaded() {
    return Boolean(this.#media);
  }

  /** Loads the player and plays it. On a warmed player it just plays. */
  load() {
    if (this.#media) {
      this.start();
      return;
    }
    this.#create(true);
    this.#wantPlay = true;
    this.#armStartTimer();
  }

  /** Loads the player paused, ahead of a play (YouTube has no play command, so it waits) */
  warm() {
    if (this.#media) return;
    const { kind } = resolveVideo(this.dataset.src || '');
    if (kind === 'vimeo' || kind === 'file') this.#create(false);
  }

  /** Plays, loading first if needed; a player that is not ready yet plays once it is */
  start() {
    if (!this.#media) {
      this.load();
      return;
    }
    this.#wantPlay = true;
    this.#armStartTimer();
    if (this.#ready) this.play();
  }

  /** @param {boolean} autoplay */
  #create(autoplay) {
    const { kind, url } = resolveVideo(this.dataset.src || '', { autoplay });
    if (!kind) return;
    this.#kind = kind;

    const stage = document.createElement('div');
    stage.className = 'fa-video-player__stage';

    if (kind === 'file') {
      const video = document.createElement('video');
      video.src = url;
      video.autoplay = autoplay;
      video.preload = 'auto';
      video.playsInline = true;
      video.addEventListener('play', () => this.#setPlaying(true));
      video.addEventListener('pause', () => this.#setPlaying(false));
      video.addEventListener('ended', () => this.#emit('ended'));
      video.addEventListener('timeupdate', () => this.#setProgress(video.currentTime / (video.duration || 1)));
      video.addEventListener('loadedmetadata', () => {
        this.#duration = video.duration;
        this.#ready = true;
        if (this.#wantPlay && video.paused) video.play().catch(() => {});
        this.#emit('ready');
      });
      this.#media = video;
    } else {
      const iframe = document.createElement('iframe');
      iframe.src = url;
      iframe.title = this.dataset.title || 'Video';
      iframe.allow = 'autoplay; fullscreen; picture-in-picture';
      iframe.allowFullscreen = true;
      // The poster behind stays visible until the player has loaded
      iframe.style.opacity = '0';
      iframe.addEventListener('load', () => iframe.style.removeProperty('opacity'), { once: true });
      // Vimeo runs with its controls off and ours on top, so keyboard focus skips the frame
      if (kind === 'vimeo') iframe.tabIndex = -1;
      this.#media = iframe;
      if (kind === 'vimeo') window.addEventListener('message', this.#onMessage);
    }
    stage.append(this.#media);
    this.append(stage);

    // YouTube keeps its own controls and cannot report playback, so its poster goes at once
    if (kind === 'youtube') this.setAttribute('data-started', '');
    // Vimeo (controls=0) and files get ours
    else this.#buildControls();
  }

  unload() {
    window.removeEventListener('message', this.#onMessage);
    this.removeEventListener('pointermove', this.#onPointerMove);
    this.removeEventListener('pointerdown', this.#onPointerDown, { capture: true });
    this.removeEventListener('focusin', this.#onFocusIn);
    this.removeEventListener('keydown', this.#showControls);
    document.removeEventListener('fullscreenchange', this.#renderFullscreen);
    document.removeEventListener('webkitfullscreenchange', this.#renderFullscreen);
    clearTimeout(this.#hideTimer);
    clearTimeout(this.#startTimer);
    this.#lastPointer = null;
    this.querySelector('.fa-video-player__stage')?.remove();
    this.querySelector('.fa-video-player__controls')?.remove();
    this.#media = null;
    this.#playing = false;
    this.#ready = false;
    this.#wantPlay = false;
    this.#duration = 0;
    this.removeAttribute('data-playing');
    this.removeAttribute('data-controls');
    this.removeAttribute('data-started');
  }

  play() {
    if (this.#media instanceof HTMLVideoElement) this.#media.play();
    else this.#post('play');
  }

  pause() {
    if (this.#media instanceof HTMLVideoElement) this.#media.pause();
    else this.#post('pause');
  }

  toggle() {
    if (this.#playing) this.pause();
    else this.play();
  }

  toggleMute() {
    this.#muted = !this.#muted;
    if (this.#media instanceof HTMLVideoElement) this.#media.muted = this.#muted;
    else this.#post('setVolume', this.#muted ? 0 : 1);
    this.#renderMute();
  }

  /** @param {number} fraction */
  seek(fraction) {
    const seconds = Math.max(0, Math.min(1, fraction)) * this.#duration;
    if (this.#media instanceof HTMLVideoElement) this.#media.currentTime = seconds;
    else this.#post('setCurrentTime', seconds);
  }

  get #canFullscreen() {
    const doc = /** @type {any} */ (document);
    if (doc.fullscreenEnabled || doc.webkitFullscreenEnabled) return true;
    // iPhone Safari can only put a <video> itself into fullscreen
    return this.#media instanceof HTMLVideoElement && typeof (/** @type {any} */ (this.#media).webkitEnterFullscreen) === 'function';
  }

  toggleFullscreen() {
    const doc = /** @type {any} */ (document);
    const current = doc.fullscreenElement || doc.webkitFullscreenElement;
    if (current) {
      (doc.exitFullscreen || doc.webkitExitFullscreen)?.call(doc);
      return;
    }
    const element = /** @type {any} */ (this);
    if (element.requestFullscreen) element.requestFullscreen().catch(() => {});
    else if (element.webkitRequestFullscreen) element.webkitRequestFullscreen();
    else if (this.#media instanceof HTMLVideoElement) /** @type {any} */ (this.#media).webkitEnterFullscreen?.();
  }

  #renderFullscreen = () => {
    const doc = /** @type {any} */ (document);
    const active = (doc.fullscreenElement || doc.webkitFullscreenElement) === this;
    this.toggleAttribute('data-fullscreen', active);
    const button = this.querySelector('[data-action="fullscreen"]');
    if (!button) return;
    button.innerHTML = active ? ICONS.collapse : ICONS.expand;
    button.setAttribute('aria-label', active ? 'Exit full screen' : 'Full screen');
  };

  #buildControls() {
    const controls = document.createElement('div');
    controls.className = 'fa-video-player__controls';
    controls.innerHTML = `
      <button type="button" class="fa-video-player__surface" data-action="toggle" aria-label="Play or pause">
        <span class="fa-video-player__big-icon" aria-hidden="true">${ICONS.play}</span>
      </button>
      <div class="fa-video-player__bar">
        <button type="button" class="fa-video-player__button" data-action="toggle" aria-label="Play">${ICONS.play}</button>
        <div class="fa-video-player__progress" role="slider" tabindex="0" aria-label="Seek" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0">
          <span class="fa-video-player__progress-fill"></span>
        </div>
        <button type="button" class="fa-video-player__button" data-action="mute" aria-label="Mute">${ICONS.sound}</button>
        <button type="button" class="fa-video-player__button" data-action="fullscreen" aria-label="Full screen">${ICONS.expand}</button>
      </div>`;
    // Where fullscreen is impossible (a Vimeo iframe on iPhone), the button is left out
    const fullscreen = controls.querySelector('[data-action="fullscreen"]');
    if (fullscreen && !this.#canFullscreen) fullscreen.remove();

    controls.addEventListener('click', this.#onControlsClick);
    controls.addEventListener('keydown', this.#onKeydown);
    this.addEventListener('pointermove', this.#onPointerMove);
    this.addEventListener('pointerdown', this.#onPointerDown, { capture: true });
    this.addEventListener('focusin', this.#onFocusIn);
    this.addEventListener('keydown', this.#showControls);
    document.addEventListener('fullscreenchange', this.#renderFullscreen);
    document.addEventListener('webkitfullscreenchange', this.#renderFullscreen);
    this.append(controls);
  }

  /**
   * Open clean. If the film has not started a few seconds after it was asked to (autoplay
   * blocked), show the controls over the poster so the visitor can start it.
   */
  #armStartTimer() {
    clearTimeout(this.#startTimer);
    this.#startTimer = window.setTimeout(() => {
      if (!this.#playing) this.#showControls();
    }, 3500);
  }

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    // When the dialog opens under a still cursor Chrome reports a "move" at the same spot,
    // so only a real change of position reveals the controls
    const last = this.#lastPointer;
    this.#lastPointer = { x: event.clientX, y: event.clientY };
    if (!last || Math.hypot(event.clientX - last.x, event.clientY - last.y) < 2) return;
    this.#showControls();
  };

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    // A touch on hidden controls only reveals them; the click that follows must not also pause
    this.#tapRevealsOnly = event.pointerType !== 'mouse' && !this.hasAttribute('data-controls');
    this.#showControls();
  };

  /** @param {FocusEvent} event */
  #onFocusIn = (event) => {
    if (event.target instanceof Element && event.target.matches(':focus-visible')) this.#showControls();
  };

  /** @param {MouseEvent} event */
  #onControlsClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const progress = target?.closest('.fa-video-player__progress');
    if (progress) {
      const rect = progress.getBoundingClientRect();
      this.seek((event.clientX - rect.left) / rect.width);
      this.#showControls();
      return;
    }
    const action = target?.closest('[data-action]')?.getAttribute('data-action');
    const revealOnly = this.#tapRevealsOnly;
    this.#tapRevealsOnly = false;
    // On touch screens the first tap on hidden controls only reveals them
    if (revealOnly) return;
    if (action === 'toggle') this.toggle();
    if (action === 'mute') this.toggleMute();
    if (action === 'fullscreen') this.toggleFullscreen();
    this.#showControls();
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const onProgress = event.target instanceof Element && event.target.closest('.fa-video-player__progress');
    if (onProgress && (event.key === 'ArrowRight' || event.key === 'ArrowLeft')) {
      event.preventDefault();
      const now = Number(this.querySelector('.fa-video-player__progress')?.getAttribute('aria-valuenow') || 0) / 100;
      this.seek(now + (event.key === 'ArrowRight' ? 0.05 : -0.05));
    }
  };

  #showControls = () => {
    this.setAttribute('data-controls', '');
    this.#scheduleHide();
  };

  #scheduleHide() {
    clearTimeout(this.#hideTimer);
    this.#hideTimer = window.setTimeout(() => {
      if (!this.#playing) return;
      // Only keyboard focus keeps them: a tap also focuses the button under it
      if (this.querySelector(':focus-visible')) {
        this.#scheduleHide();
        return;
      }
      this.removeAttribute('data-controls');
    }, 2500);
  }

  /** @param {MessageEvent} event */
  #onMessage = (event) => {
    if (!(this.#media instanceof HTMLIFrameElement) || event.source !== this.#media.contentWindow) return;
    let data;
    try {
      data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
    } catch {
      return;
    }
    if (!data) return;

    // The embed still reports progress as "playProgress" and the end as "finish"
    if (data.event === 'ready') {
      for (const name of ['play', 'pause', 'ended', 'timeupdate']) this.#post('addEventListener', name);
      this.#post('getDuration');
      this.#post('getVolume');
      this.#ready = true;
      if (this.#wantPlay && !this.#playing) this.play();
      this.#emit('ready');
    } else if (data.event === 'play' || data.event === 'playing') {
      this.#setPlaying(true);
    } else if (data.event === 'pause') {
      this.#setPlaying(false);
    } else if (data.event === 'ended' || data.event === 'finish') {
      this.#setPlaying(false);
      this.#emit('ended');
    } else if ((data.event === 'timeupdate' || data.event === 'playProgress') && data.data) {
      this.#duration = data.data.duration || this.#duration;
      this.#setProgress(data.data.percent ?? 0);
    } else if (data.method === 'getDuration') {
      this.#duration = Number(data.value) || 0;
    } else if (data.method === 'getVolume') {
      this.#muted = Number(data.value) === 0;
      this.#renderMute();
    }
  };

  /**
   * @param {string} method
   * @param {unknown} [value]
   */
  #post(method, value) {
    if (!(this.#media instanceof HTMLIFrameElement) || this.#kind !== 'vimeo') return;
    const message = value === undefined ? { method } : { method, value };
    this.#media.contentWindow?.postMessage(JSON.stringify(message), VIMEO_ORIGIN);
  }

  /** @param {boolean} playing */
  #setPlaying(playing) {
    this.#playing = playing;
    this.toggleAttribute('data-playing', playing);
    // The poster lifts once the film really plays, and stays down after a pause
    if (playing) this.setAttribute('data-started', '');
    const icon = this.querySelector('.fa-video-player__bar [data-action="toggle"]');
    if (icon) {
      icon.innerHTML = playing ? ICONS.pause : ICONS.play;
      icon.setAttribute('aria-label', playing ? 'Pause' : 'Play');
    }
    const big = this.querySelector('.fa-video-player__big-icon');
    if (big) big.innerHTML = playing ? ICONS.pause : ICONS.play;
    this.#emit(playing ? 'play' : 'pause');
    // Starting to play never brings the controls up; pausing shows them so the film can resume
    if (playing) {
      this.#wantPlay = false;
      clearTimeout(this.#startTimer);
      if (this.hasAttribute('data-controls')) this.#scheduleHide();
    } else if (this.#media) {
      this.#showControls();
    }
  }

  /** @param {number} fraction */
  #setProgress(fraction) {
    const fill = /** @type {HTMLElement | null} */ (this.querySelector('.fa-video-player__progress-fill'));
    if (fill) fill.style.transform = `scaleX(${Math.max(0, Math.min(1, fraction))})`;
    this.querySelector('.fa-video-player__progress')?.setAttribute('aria-valuenow', String(Math.round(fraction * 100)));
  }

  #renderMute() {
    const button = this.querySelector('[data-action="mute"]');
    if (!button) return;
    button.innerHTML = this.#muted ? ICONS.muted : ICONS.sound;
    button.setAttribute('aria-label', this.#muted ? 'Unmute' : 'Mute');
  }

  /** @param {string} name */
  #emit(name) {
    this.dispatchEvent(new CustomEvent(`fa-video:${name}`, { bubbles: true }));
  }
}

export class FaVideoDialog extends HTMLElement {
  /** @type {HTMLElement | null} */
  #opener = null;
  /** Focus returning to the play button on close must not load the film again at once */
  #closedAt = 0;

  connectedCallback() {
    this.dialog?.addEventListener('close', this.#onClose);
    this.dialog?.addEventListener('click', this.#onDialogClick);
    this.dialog?.addEventListener('keydown', this.#onKeydown);
  }

  disconnectedCallback() {
    this.dialog?.removeEventListener('close', this.#onClose);
    this.dialog?.removeEventListener('click', this.#onDialogClick);
    this.dialog?.removeEventListener('keydown', this.#onKeydown);
  }

  /** @returns {HTMLDialogElement | null} */
  get dialog() {
    return this.querySelector('dialog');
  }

  /** @returns {FaVideoPlayer | null} */
  get player() {
    const player = this.querySelector('fa-video-player');
    return player instanceof FaVideoPlayer ? player : null;
  }

  /** Fetches the poster and loads the player paused, so opening is instant */
  warm() {
    if (performance.now() - this.#closedAt < 1500) return;
    const poster = this.querySelector('.fa-video-player__poster');
    if (poster instanceof HTMLImageElement && poster.loading !== 'eager') {
      poster.loading = 'eager';
      poster.fetchPriority = 'high';
    }
    this.player?.warm();
  }

  /** @param {HTMLElement | null} [opener] - Where focus returns on close */
  open(opener = null) {
    if (!this.dialog) return;
    const active = document.activeElement;
    this.#opener = opener || (active instanceof HTMLElement ? active : null);
    this.warm();
    this.dialog.showModal();
    this.player?.start();
    this.querySelector('[data-fa-video-close]')?.focus();
  }

  close() {
    this.dialog?.close();
  }

  #onClose = () => {
    this.#closedAt = performance.now();
    this.player?.unload();
    // Safari does not focus a clicked button, so hand focus back to the opener explicitly
    const opener = this.#opener;
    this.#opener = null;
    if (opener?.isConnected) opener.focus({ preventScroll: true });
  };

  /**
   * Tab and Shift+Tab wrap inside the dialog, so focus never leaves for the browser's own UI
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (event.key !== 'Tab' || !this.dialog) return;
    const focusable = [
      ...this.dialog.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'),
    ].filter(
      (element) =>
        element instanceof HTMLElement && !element.hasAttribute('disabled') && element.getClientRects().length > 0
    );
    if (focusable.length === 0) return;
    const first = /** @type {HTMLElement} */ (focusable[0]);
    const last = /** @type {HTMLElement} */ (focusable[focusable.length - 1]);
    const active = document.activeElement;
    if (event.shiftKey && (active === first || !this.dialog.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !this.dialog.contains(active))) {
      event.preventDefault();
      first.focus();
    }
  };

  /** @param {MouseEvent} event */
  #onDialogClick = (event) => {
    const target = event.target;
    // A click on the backdrop lands on the dialog itself; the close button closes too
    if (target === this.dialog || (target instanceof Element && target.closest('[data-fa-video-close]'))) {
      this.close();
    }
  };
}

if (!customElements.get('fa-video-player')) customElements.define('fa-video-player', FaVideoPlayer);

if (!customElements.get('fa-video-dialog')) {
  customElements.define('fa-video-dialog', FaVideoDialog);

  /**
   * @param {EventTarget | null} target
   * @returns {{ button: HTMLElement, host: FaVideoDialog } | null}
   */
  const dialogFor = (target) => {
    const button = target instanceof Element ? target.closest('[data-fa-video-open]') : null;
    if (!(button instanceof HTMLElement)) return null;
    const host = document.getElementById(button.getAttribute('data-fa-video-open') || '')?.closest('fa-video-dialog');
    return host instanceof FaVideoDialog ? { button, host } : null;
  };

  // Play buttons sit outside their dialog. Listening on the window in the capture phase runs
  // before the gallery's own click handling, so a tap on the button never also opens the zoom.
  window.addEventListener(
    'click',
    (event) => {
      const found = dialogFor(event.target);
      if (!found) return;
      event.preventDefault();
      event.stopPropagation();
      found.host.open(found.button);
    },
    { capture: true }
  );

  // Pointing at, touching or tabbing to a play button warms its film ahead of the click
  for (const type of ['pointerover', 'touchstart', 'focusin']) {
    window.addEventListener(type, (event) => dialogFor(event.target)?.host.warm(), { capture: true, passive: true });
  }

  // A touch screen gives only the length of a tap as warning, so there the film warms once its
  // button has been on screen for a moment, after the page has finished loading and gone idle
  if ('IntersectionObserver' in window) {
    const touchOnly = window.matchMedia('(hover: none)');
    /** @param {() => void} callback */
    const whenIdle = (callback) =>
      'requestIdleCallback' in window ? window.requestIdleCallback(callback, { timeout: 2000 }) : setTimeout(callback, 300);
    /** @type {Map<Element, number>} */
    const pending = new Map();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          clearTimeout(pending.get(entry.target));
          if (!entry.isIntersecting || !touchOnly.matches) continue;
          pending.set(
            entry.target,
            window.setTimeout(() => {
              observer.unobserve(entry.target);
              whenIdle(() => dialogFor(entry.target)?.host.warm());
            }, 600)
          );
        }
      },
      { threshold: 0.5 }
    );
    const watch = () => document.querySelectorAll('[data-fa-video-open]').forEach((button) => observer.observe(button));
    if (document.readyState === 'complete') watch();
    else window.addEventListener('load', watch, { once: true });
  }
}
